/**
 * Aba "Dossiê da Cliente" da ficha da cliente, idêntica à do legado (`admin.html:2766-2773`,
 * `5559-5617`; linhas 211, 212 e 213 do inventário).
 *
 * "Considerando: ..." (as abas de dados vinculadas aos produtos do lead), o botão que gera a
 * prévia do dossiê em `.doc` e a lista "Prévias geradas" (pasta `dossie-previas`, a do lead e a
 * antiga). A montagem do documento é regra pura, em `dossie/documento.ts`.
 *
 * O STATUS FICA NA TELA, como no legado: "Prévia gerada!" e "Erro ao gerar: ..." não somem sozinhos
 * (`admin.html:5612` e `5614`) e o `carregarDossiePrevias` não os apaga (`5570-5577`). Por isso ele
 * mora na memória da página (`comum-a3/memoria-da-pagina.ts`): sair da aba e voltar encontra o
 * mesmo texto, e só a geração seguinte o troca.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaDossie`.
 */

// Estado, memória de função e referência do React.
import { useCallback, useRef, useState } from 'react';
// O botão do Chassi.
import { Botao } from '@complexity/ui';
// O caminho da pasta do lead e o nome aceito pelo Storage, portados do legado.
import { caminhoArquivosCliente, sanitizarNomeArquivo } from '@studiolu/dados';
// As leituras do documento, a lista da pasta e o envio.
import {
  lerChecklistAtendimento,
  lerChecklistColoracao,
  lerChecklistMilhoes,
  lerQuestionarioAvaliativo,
  lerQuestionarioGeral,
  lerQuestionarioPersonalidade,
  TIPO_PREVIA_DOSSIE,
} from '@studiolu/dados/areas/ficha-dossie';
import { enviarArquivo, listarPasta, pastasDaSubpasta } from '@studiolu/dados/areas/ficha-arquivos';
import { lerAbasHabilitadas } from '@studiolu/dados/areas/ficha-abas-habilitadas';
// O carimbo de data e hora do nome do arquivo, idêntico ao do legado.
import { formatarDataHoraArquivo } from '../../../comum/formatos.js';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O contrato da aba, o contrato de carga, as chaves de parâmetro e o radar.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
import { calcularPontosRaiz, chavesDeParametro } from '../comum-ficha/regras.js';
// As peças da A3.
import { textoAbasConsideradas } from './comum-a3/rotulos.js';
import { ligacaoVazia, ListaDeArquivos } from './comum-a3/lista-de-arquivos.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { criarMemoria, useMemoria } from './comum-a3/memoria-da-pagina.js';
// A montagem do documento e o radar em PNG.
import { montarHtmlDossie, svgDoRadar, type LinhaLida } from './dossie/documento.js';
import { svgParaPngBase64 } from './dossie/radar-png.js';

// A frase de vazio das abas consideradas nesta aba (`admin.html:5567`); a da IA é outra.
const SEM_ABAS = 'Nenhuma aba de dados vinculada ao(s) produto(s) deste lead. O dossiê sairá só com o cabeçalho da cliente.';

// O lado do PNG do radar no documento, em pixels (`admin.html:5597`).
const LADO_DO_RADAR_PX = 480;

// O `status-dossie` do legado: DOM da página, que nada limpa ao trocar de aba nem ao abrir outra
// cliente. Sem relógio, porque este status do legado não some sozinho.
const statusDaGeracao = criarMemoria('');

// Uma linha lida para o documento: a falha da leitura vale "não respondeu", como no legado, que
// ignora o erro (`const { data: geral } = ...`, `5585-5590`). A cópia rasa vira objeto indexável.
function linhaOuNula(resultado: { readonly ok: boolean; readonly dados?: object | null }): LinhaLida {
  return resultado.ok && resultado.dados ? { ...resultado.dados } : null;
}

export function AbaDossie({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Dossiê da Cliente" />;
  return <ConteudoDossie ficha={ficha} />;
}

function ConteudoDossie({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  // As chaves de parâmetro do lead, em texto para as dependências compararem por valor.
  const chaves = chavesDeParametro(ficha.lead?.produtos ?? []).join('|');
  const [abasConsideradas, setAbasConsideradas] = useState('');
  const [rotuloDoBotao, setRotuloDoBotao] = useState('Gerar prévia do dossiê');
  // O status do legado não some sozinho: "Prévia gerada!" fica (`5612`), e continua lá depois de
  // sair da aba e voltar, porque no legado ele é DOM da página.
  const [status, setStatus] = useMemoria(statusDaGeracao);
  // A lista "Prévias geradas", comandada por esta aba, como no `carregarDossiePrevias` (`5571`).
  const listaDePrevias = useRef(ligacaoVazia());

  // As abas habilitadas; falha vale conjunto vazio, como o `data || []` do legado.
  const lerAbas = async (): Promise<Set<string>> => {
    const resultado = await lerAbasHabilitadas(dados, chaves ? chaves.split('|') : []);
    return new Set(resultado.ok ? resultado.dados.map((linha) => linha.aba) : []);
  };

  // `carregarDossiePrevias` (`admin.html:5570-5577`): a lista das prévias, o rótulo do botão (que
  // olha SÓ a pasta do lead, e não a antiga) e a frase das abas consideradas, nesta ordem.
  //
  // O `carregarDossiePrevias` do legado NÃO limpa o status: quem gerou a prévia, saiu da aba e
  // voltou continua lendo "Prévia gerada!".
  const carregar = useCallback(async () => {
    await listaDePrevias.current.ler();
    const lista = await listarPasta(dados, caminhoArquivosCliente(escopo, 'dossie-previas'));
    const temPrevia = lista.ok && lista.dados !== null && lista.dados.length > 0;
    setRotuloDoBotao(temPrevia ? 'Atualizar prévia do dossiê' : 'Gerar prévia do dossiê');
    const abas = await lerAbasHabilitadas(dados, chaves ? chaves.split('|') : []);
    setAbasConsideradas(textoAbasConsideradas(new Set(abas.ok ? abas.dados.map((linha) => linha.aba) : [])) ?? SEM_ABAS);
  }, [dados, escopo, chaves]);

  // A carga da aba, pelo contrato: a moldura a roda antes de mostrar a aba e a cada toque no botão.
  useCarregadorDaAba(carregar);

  // "Gerar prévia do dossiê" (`admin.html:5579-5617`). O botão não trava durante a geração, como
  // no legado.
  const gerar = async () => {
    setStatus('Gerando...');
    try {
      const abasHabilitadas = await lerAbas();
      // As seis leituras em sequência, na ordem do legado (`5585-5590`).
      const geral = linhaOuNula(await lerQuestionarioGeral(dados, escopo));
      const personalidade = linhaOuNula(await lerQuestionarioPersonalidade(dados, escopo));
      const atendimento = linhaOuNula(await lerChecklistAtendimento(dados, escopo));
      const milhoes = linhaOuNula(await lerChecklistMilhoes(dados, escopo));
      const avaliativo = linhaOuNula(await lerQuestionarioAvaliativo(dados, escopo));
      const coloracao = linhaOuNula(await lerChecklistColoracao(dados, escopo));
      const pontosRaiz = calcularPontosRaiz(geral, personalidade);
      // O radar só vira imagem se a aba dele vale E há alguma pontuação (`5595`).
      let imagemRadarDataUri: string | null = null;
      if (abasHabilitadas.has('radar_estilo_raiz') && !pontosRaiz.every((p) => p.pontuacao === 0)) {
        try {
          imagemRadarDataUri = await svgParaPngBase64(svgDoRadar(pontosRaiz), LADO_DO_RADAR_PX);
        } catch (erro) {
          // Sem a imagem, o documento segue só com a tabela (`5598-5600`).
          console.error('Erro ao gerar imagem do radar, prévia vai seguir só com a tabela:', erro);
        }
      }
      const html = montarHtmlDossie({
        cliente: ficha.cliente,
        geral,
        personalidade,
        pontosRaiz,
        atendimento,
        milhoes,
        avaliativo,
        coloracao,
        abasHabilitadas,
        imagemRadarDataUri,
        dataGeracao: new Date().toLocaleString('pt-BR'),
      });
      // O nome do arquivo: `previa_dossie_<nome>_<AAAA-MM-DD_HHMM>.doc` (`5605-5607`).
      const nomeCliente = sanitizarNomeArquivo(ficha.cliente.nome_completo || 'cliente');
      const nomeArquivo = `previa_dossie_${nomeCliente}_${formatarDataHoraArquivo(new Date())}.doc`;
      const caminho = `${caminhoArquivosCliente(escopo, 'dossie-previas')}/${nomeArquivo}`;
      // HTML gravado como documento do Word, sem `upsert`, como no legado (`5609-5610`).
      const envio = await enviarArquivo(dados, caminho, new Blob([html], { type: TIPO_PREVIA_DOSSIE }), {
        contentType: TIPO_PREVIA_DOSSIE,
      });
      // Este ponto mostra a mensagem crua do Storage depois do prefixo.
      if (!envio.ok) {
        setStatus('Erro ao gerar: ' + envio.erro.mensagem);
        return;
      }
      setStatus('Prévia gerada!');
      // O legado chama `carregarDossiePrevias()` de novo depois de gravar (`5613`).
      await carregar();
    } catch (erro) {
      // Qualquer exceção no caminho vira a mesma frase (`5614-5615`).
      setStatus('Erro ao gerar: ' + (erro instanceof Error ? erro.message : String(erro)));
    }
  };

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Dossiê da Cliente</h3>
      <p data-testid="dossie-abas-consideradas" className="text-cx-xs text-texto-suave">
        {abasConsideradas}
      </p>
      {/* Consulta não vê botão de ação (`admin.html:3395`). */}
      {!somenteLeitura && (
        <div>
          <Botao onClick={() => void gerar()}>{rotuloDoBotao}</Botao>
        </div>
      )}
      <p role="status" data-testid="status-dossie" className="min-h-[1.25rem] text-cx-sm text-texto-suave">
        {status}
      </p>
      <h4 className="text-cx-sm font-semibold text-texto">Prévias geradas</h4>
      <ListaDeArquivos pastas={pastasDaSubpasta(escopo, 'dossie-previas')} ligacao={listaDePrevias.current} testid="lista-dossie" />
    </section>
  );
}
